import Dexie, { type EntityTable } from "dexie";

export type CachedPackage = {
  id: string;
  userId: string;
  shiftId: string;
  payload: unknown;
  expiresAt: string;
  cachedAt: string;
};

export type QueuedOperation = {
  id: string;
  userId: string;
  shiftId: string;
  kind: "complete" | "skip" | "block" | "note" | "handover" | "finish";
  payload: unknown;
  baseRevision: number;
  attempts: number;
  firstOnlineAt?: string;
  nextAttemptAt: string;
  createdAt: string;
  status: "pending" | "syncing" | "conflict" | "failed";
};

export type CachedAsset = {
  id: string;
  userId: string;
  shiftId: string;
  blob: Blob;
  mediaType: string;
  expiresAt: string;
};

class WorkerDatabase extends Dexie {
  packages!: EntityTable<CachedPackage, "id">;
  operations!: EntityTable<QueuedOperation, "id">;
  assets!: EntityTable<CachedAsset, "id">;

  constructor() {
    super("capaciteo-worker-v1");
    this.version(1).stores({
      packages: "id, userId, shiftId, expiresAt",
      operations: "id, userId, shiftId, status, nextAttemptAt",
    });
    this.version(2).stores({
      packages: "id, userId, shiftId, expiresAt",
      operations: "id, userId, shiftId, status, nextAttemptAt",
      assets: "id, userId, shiftId, expiresAt",
    });
  }

  async purge() {
    await this.transaction(
      "rw",
      this.packages,
      this.operations,
      this.assets,
      async () => {
        await this.packages.clear();
        await this.operations.clear();
        await this.assets.clear();
      },
    );
  }

  async purgeExpired(now = new Date()) {
    await this.transaction("rw", this.packages, this.assets, async () => {
      await this.packages
        .where("expiresAt")
        .belowOrEqual(now.toISOString())
        .delete();
      await this.assets
        .where("expiresAt")
        .belowOrEqual(now.toISOString())
        .delete();
    });
  }
}

export const workerDatabase = new WorkerDatabase();

export function mostRecentPackage(packages: CachedPackage[]) {
  return packages.reduce<CachedPackage | undefined>((latest, candidate) => {
    if (!latest) return candidate;
    return new Date(candidate.cachedAt).getTime() >
      new Date(latest.cachedAt).getTime()
      ? candidate
      : latest;
  }, undefined);
}

export async function latestPackageForUser(userId: string) {
  const packages = await workerDatabase.packages
    .where("userId")
    .equals(userId)
    .toArray();
  return mostRecentPackage(packages);
}
